/**
 * The two pieces the milestone board is drawn out of: the head of a column, and
 * one task card inside one.
 *
 * The same split `Roadmap.tsx` and `RoadmapCard.tsx` made, for the same reason
 * and with the same seam: NOTHING in this file talks to the API. Every component
 * here takes what to draw and a callback, so a card cannot start a write on its
 * own and `MilestoneBoard.tsx` stays the only place where a click on this board
 * turns into a PATCH. It also keeps both files under the size AGENTS.md §4 asks
 * a file to stop at.
 *
 * `UNFILED` lives here rather than beside the board because both halves need it
 * and this is the half that puts it on screen: it is the menu's spelling of the
 * column whose key is null.
 */
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, CheckSquare, EyeOff, Flag, ListTodo } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { MilestoneBoardColumn } from "../../../lib/milestoneBoard";
import type { Milestone, Task } from "../../../lib/projects";
import { StatusPill, fmtDate } from "../../ui";
import { MoveMenu } from "./RoadmapCard";
import { isOverdue } from "./lib";
import type { ColumnKey } from "./milestoneBoardMove";
import { taskTone } from "./status";

/**
 * The menu's spelling of the unfiled column.
 *
 * `MoveMenu` is generic over a string, so the null the board uses as a column
 * key cannot travel through it. The empty string is safe as a stand-in because
 * every real value here is a cuid; it is translated back to null at the one
 * place that writes (`move` in `MilestoneBoard.tsx`), and nowhere else, so no
 * other code has to know two spellings of the same idea.
 */
export const UNFILED = "";

/** A card is wider than pill padding allows, so it takes the softened corners
 *  rather than the 999px ones - see `.crm-list-row-soft`. The same line as in
 *  `RoadmapCard.tsx` and `tasks/TaskBoard.tsx`: three boards, one card shape. */
const CARD = "crm-prow crm-list-row-soft relative flex-col items-stretch gap-2.5 p-3.5";

/** A milestone nobody finished whose date has gone by - the same rule the list
 *  row and the roadmap card apply, kept local because it is one line. */
function milestoneLate(m: Milestone): boolean {
  return !m.done && !!m.dueDate && new Date(m.dueDate) < new Date();
}

/**
 * The head of one column: what the phase is called, when it runs, how much of it
 * is ticked.
 *
 * The name is NOT in a `StatusPill` the way the roadmap's column labels are. A
 * pill says which of five states something is in, and a phase name is not a
 * state - dressing it as one would teach the eye to read "Design" as a status.
 *
 * Nothing here writes. Ticking a phase off, renaming it and reordering it all
 * live on the list view one pill away, and a second set of controls for them
 * would be a second place to keep the confirmation and the failure handling
 * right. The board is about where the work sits.
 */
export function ColumnHead({ column, name }: { column: MilestoneBoardColumn; name: string }) {
  const t = useT();
  const m = column.milestone;
  const late = m ? milestoneLate(m) : false;

  return (
    <div className="flex flex-col gap-1.5 px-0.5 pb-0.5">
      <div className="flex items-center gap-2">
        {/* The unfiled column gets the task glyph rather than the flag: it is
            not a phase, and a flag on it would make the project look like it has
            one more phase than it has. */}
        {m ? (
          m.done ? (
            <CheckCircle2 className="size-3.5 shrink-0 text-[var(--color-success)]" />
          ) : (
            <Flag className="size-3.5 shrink-0 text-[var(--color-primary)]" />
          )
        ) : (
          <ListTodo className="crm-mut size-3.5 shrink-0" />
        )}
        <span className={cn("crm-prow-n crm-trunc", (!m || m.done) && "crm-mut", m?.done && "line-through")}>
          {name}
        </span>
        {/* The tally the phase list draws in its own column, in the one place a
            reader of the board would look for it. `done` over `total` and never
            `tasks.length`: see the note at the top about a truncated board. */}
        <span className="crm-mut crm-tnum crm-push shrink-0 text-[11.5px] font-bold">
          {t("projects.milestoneTasks", { done: column.done, total: column.total })}
        </span>
      </div>

      {/* Only where there is a date. The imported phases carry a deadline and no
          start, and most carry neither, so a row of empty date slots would be
          the first thing on every column. */}
      {m && (m.startDate || m.dueDate) ? (
        <div className="flex flex-wrap items-center gap-2.5">
          {m.startDate ? (
            <span className="crm-mut crm-tnum text-[11px]">
              {t("projects.milestoneStart")} {fmtDate(m.startDate)}
            </span>
          ) : null}
          {m.dueDate ? (
            <span className={cn("crm-tnum flex items-center gap-1.5 text-[11px]", late ? "text-red-600" : "crm-mut")}>
              <CalendarClock className="size-3 shrink-0" />
              {fmtDate(m.dueDate)}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** One task on the board. The same shape the roadmap's task card has, with the
 *  menu pointed along this board's axis: the phase, not the status. */
export function BoardCard({
  task,
  column,
  options,
  onMove,
}: {
  task: Task;
  /** Which column this card is DRAWN in, which is not always what the task
   *  carries: a task filed under another project's phase lands in the unfiled
   *  column while its own `milestoneId` still names that phase. The menu ticks
   *  where the card is, so the tick can never disagree with the board. */
  column: ColumnKey;
  options: { value: string; label: string }[];
  onMove: (task: Task, from: ColumnKey, to: ColumnKey) => void;
}) {
  const t = useT();
  // Read the same way every other task surface reads it (see `TasksListRow`):
  // undefined is "nobody counted the boxes for this row", `{ done: 0, total: 0 }`
  // is the real answer "there is no checklist". The board's load runs
  // `withChecklistProgress` over the cards it is about to send, so the figure is
  // here - but neither shape earns a placeholder, and 0 of 0 is not progress.
  const checklist = task.checklistProgress;

  return (
    <div className={CARD}>
      <div className="flex items-start gap-2.5">
        <Link
          to={`/tasks/${task.id}`}
          className={cn(
            "crm-prow-n min-w-0 flex-1 after:absolute after:inset-0 after:rounded-[22px]",
            task.status === "done" && "crm-mut line-through",
          )}
        >
          {task.title}
        </Link>

        {/* The closed eye every other task surface draws. `relative z-[1]`
            because the title link covers the whole card with an ::after, and a
            tooltip under that overlay never opens. */}
        {task.visibleToClient ? null : (
          <span
            className="crm-mut relative z-[1] mt-0.5 inline-flex shrink-0"
            role="img"
            title={t("projects.hiddenFromClient")}
            aria-label={t("projects.hiddenFromClient")}
          >
            <EyeOff className="size-3.5" />
          </span>
        )}

        <MoveMenu
          label={t("tasks.moveTo")}
          value={column ?? UNFILED}
          options={options}
          onPick={(v) => onMove(task, column, v === UNFILED ? null : v)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* The status stays on the card even though it is not what the columns
            are made of. The board answers "what is in this phase", and half of
            that answer is how far along the pieces are - without it a column of
            eight cards cannot be told from a column of eight finished ones. */}
        <StatusPill tone={taskTone(task.status)}>{t(`tasks.status.${task.status}`)}</StatusPill>
        {task.dueDate ? (
          <span
            className={cn(
              "crm-tnum flex items-center gap-1.5 text-[11px]",
              isOverdue(task) ? "text-red-600" : "crm-mut",
            )}
          >
            <CalendarClock className="size-3 shrink-0" />
            {fmtDate(task.dueDate)}
          </span>
        ) : null}

        {/* How much of the plan inside the card is ticked - the same badge, the
            same classes and the same key as `tasks/TaskBoard.tsx`, because a
            reader moving between the two boards should not have to learn a
            second spelling of one fact. It is about the CHECKLIST and not the
            task: the status pill to its left is what says how far the work has
            got, and a ticked-through plan does not move it.

            Drawn only where there are boxes, the way the date beside it
            disappears when there is none. A card carrying "0/0" would be
            claiming a plan exists and is untouched, which is a different thing
            from having no plan.

            `relative z-[1]` is the one line that differs from the register's
            board, and deliberately: this card's title link covers the whole card
            with an ::after, so without it the tooltip that names these two
            numbers never opens - the same reason the closed eye above carries
            it. The cost is the badge's own width not navigating, which is the
            trade this file already makes for the eye. */}
        {checklist && checklist.total > 0 ? (
          <span
            className="crm-mut crm-tnum relative z-[1] flex items-center gap-1 text-[11px]"
            title={t("tasks.checklist")}
          >
            <CheckSquare className="size-3" />
            {checklist.done}/{checklist.total}
          </span>
        ) : null}
      </div>
    </div>
  );
}
